import { useState, useRef, useCallback, useEffect } from 'react';

const useTextToSpeech = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const speechSynthesis = useRef(null);
  const utteranceRef = useRef(null);
  const progressInterval = useRef(null);
  const startTime = useRef(null);

  useEffect(() => {
    // Check if speech synthesis is supported
    if ('speechSynthesis' in window) {
      setIsSupported(true);
      speechSynthesis.current = window.speechSynthesis;
    }

    return () => {
      // Cleanup on unmount
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      if (speechSynthesis.current) {
        speechSynthesis.current.cancel();
      }
    };
  }, []);

  const speak = useCallback((text, options = {}) => {
    if (!isSupported || !speechSynthesis.current) {
      console.error('Speech synthesis not supported');
      return;
    }

    setIsLoading(true);

    // Stop any current speech
    stop();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    setCurrentUtterance(utterance);
    setCurrentText(text);

    // Set voice options
    utterance.rate = options.rate || 1;
    utterance.pitch = options.pitch || 1;
    utterance.volume = options.volume || 1;
    utterance.lang = options.lang || 'en-US';

    // Set voice if specified
    if (options.voice) {
      const voices = speechSynthesis.current.getVoices();
      const selectedVoice = voices.find(voice => voice.name === options.voice);
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
    }

    // Estimate duration based on text length and rate
    const wordsPerMinute = 200 * (options.rate || 1);
    const wordCount = text.split(' ').length;
    const estimatedDuration = (wordCount / wordsPerMinute) * 60;
    setDuration(estimatedDuration);
    setCurrentTime(0);

    // Event listeners
    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
      setIsLoading(false);
      startTime.current = Date.now();
      
      // Start progress tracking
      progressInterval.current = setInterval(() => {
        if (startTime.current) {
          const elapsed = (Date.now() - startTime.current) / 1000;
          setCurrentTime(elapsed);
          const progressPercent = Math.min((elapsed / estimatedDuration) * 100, 100);
          setProgress(progressPercent);
        }
      }, 100);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      setCurrentTime(estimatedDuration);
      setCurrentUtterance(null);
      utteranceRef.current = null;
      startTime.current = null;
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error);
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoading(false);
      setCurrentUtterance(null);
      utteranceRef.current = null;
      startTime.current = null;
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };

    utterance.onpause = () => {
      setIsPaused(true);
      setIsPlaying(false);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };

    utterance.onresume = () => {
      setIsPaused(false);
      setIsPlaying(true);
      startTime.current = Date.now() - (currentTime * 1000);
      
      progressInterval.current = setInterval(() => {
        if (startTime.current) {
          const elapsed = (Date.now() - startTime.current) / 1000;
          setCurrentTime(elapsed);
          const progressPercent = Math.min((elapsed / estimatedDuration) * 100, 100);
          setProgress(progressPercent);
        }
      }, 100);
    };

    // Start speaking
    speechSynthesis.current.speak(utterance);
  }, [isSupported, currentTime]);

  const pause = useCallback(() => {
    if (speechSynthesis.current && isPlaying) {
      speechSynthesis.current.pause();
    }
  }, [isPlaying]);

  const resume = useCallback(() => {
    if (speechSynthesis.current && isPaused) {
      speechSynthesis.current.resume();
    }
  }, [isPaused]);

  const stop = useCallback(() => {
    if (speechSynthesis.current) {
      speechSynthesis.current.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoading(false);
      setProgress(0);
      setCurrentTime(0);
      setCurrentUtterance(null);
      utteranceRef.current = null;
      startTime.current = null;
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else if (isPaused) {
      resume();
    }
  }, [isPlaying, isPaused, pause, resume]);

  const getVoices = useCallback(() => {
    if (speechSynthesis.current) {
      return speechSynthesis.current.getVoices();
    }
    return [];
  }, []);

  return {
    isPlaying,
    isPaused,
    currentText,
    progress,
    duration,
    currentTime,
    isLoading,
    isSupported,
    speak,
    pause,
    resume,
    stop,
    togglePlayPause,
    getVoices
  };
};

export default useTextToSpeech;
